import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { V } from '../../utils/theme';
import { Icons } from '../../components/Icons';
import { Card, Btn, Chip, Skeleton, Stat } from '../../components/ui';
import { SectionTitle, Notice, Callout, EmptyState } from '../../components/kit';
import { chartCfg } from '../../utils/helpers';
import { apiPost, ApiError } from '../../utils/api';
import {
  DASH, numText, pctText, ratioText, ageText, dateText, monthLabel,
  heartbeatState, RANGE_DAYS, consentRows, revokedRows, adherenceRows, inviteFunnel,
  SERIES_KEYS, seriesChartData, seriesTotals, platformCsv, orgCsv, csvFilename, downloadText,
} from './analytics/helpers';
import { Tile, TileGrid, BarRow, ChartLegend, StatusPill, td, th, Label, ToolBtn, tone, seriesColors } from './analytics/shared';

// ─── Super-admin: white-label analytics ───
// Two modes over three admin actions:
//   platform → org_overview { days }   (KPIs + the daily series)
//   per-org  → org_list {} then org_analytics { org_id }
// Loading contract copied from ./orgs/: `null` means "still loading" and `[]`
// means "loaded, nothing there" — with 1 org, 2 members and 0 programmes in
// production the empty state is the common case, not the edge case. A failed
// refresh keeps whatever was already on screen and adds a banner; it never
// blanks the panel.
//
// Every ratio in these payloads is null when its denominator is 0 (app.pct in
// migrations/015). numText/pctText/ratioText render that as "—" — never NaN,
// never a 0 that would read as a real measurement.

/** One call to POST /api/admin, normalised to { ok, data } / { ok:false, msg }. */
function useAdminApi(email) {
  return useCallback(async (action, extra = {}) => {
    try { return { ok: true, data: await apiPost("/api/admin", { action, ...extra }, { email }) }; }
    catch (e) {
      if (e instanceof ApiError) return { ok: false, msg: e.body?.error || `HTTP ${e.status}` };
      return { ok: false, msg: e?.message || 'Network error' };
    }
  }, [email]);
}

export function OrgAnalytics({ s }) {
  const af = useAdminApi(s?.profile?.email);
  const [mode, setMode] = useState('platform');
  return (
    <div data-testid="org-analytics" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 20 }}>📊</span>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: V.text }}>Org Analytics</div>
          <div style={{ fontSize: 10, color: V.text3 }}>White-label platform &amp; per-tenant metrics</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Chip label="Platform" active={mode === 'platform'} onClick={() => setMode('platform')} />
          <Chip label="Per-org" active={mode === 'org'} onClick={() => setMode('org')} />
        </div>
      </div>
      {mode === 'platform' ? <PlatformView af={af} /> : <PerOrgView af={af} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Platform
// ════════════════════════════════════════════════════════════════════════════

function PlatformView({ af }) {
  const [days, setDays] = useState(30);
  const [ov, setOv] = useState(null);       // null = loading (never loaded yet)
  const [series, setSeries] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [at, setAt] = useState(null);

  const load = useCallback(async (d) => {
    setBusy(true);
    const r = await af('org_overview', { days: d });
    // On failure keep the previous payload on screen — a transient 500 on a
    // range change must not throw away a working dashboard.
    if (r.ok) { setOv(r.data?.overview || {}); setSeries(r.data?.series || null); setErr(''); setAt(new Date()); }
    else setErr(r.msg);
    setBusy(false);
  }, [af]);

  useEffect(() => { load(days); }, [load, days]);

  const chartData = useMemo(() => seriesChartData(series?.series), [series]);
  const totals = useMemo(() => seriesTotals(series?.series), [series]);

  if (ov === null) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {err ? <ErrCard msg={err} onRetry={() => load(days)} /> : <><Card><Skeleton rows={4} /></Card><Card><Skeleton rows={6} /></Card></>}
      </div>
    );
  }

  const colors = seriesColors();
  const orgs = ov.orgs || {}, seats = ov.seats || {}, members = ov.members || {}, users = ov.users || {};
  const invites = ov.invites || {}, coaching = ov.coaching || {}, programs = ov.programs || {};
  const messaging = ov.messaging || {}, queue = ov.email_queue || {}, annEmail = ov.announcement_emails || {};
  const billing = ov.billing || {}, audit = ov.audit || {}, checkins = ov.checkins || {}, goals = ov.goals || {};
  const hb = heartbeatState(ov.heartbeat);
  const hbColor = tone(hb.tone);
  const funnel = inviteFunnel(invites);
  const funnelMax = Math.max(1, ...funnel.map(f => f.value));
  const adherence = ov.adherence || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Toolbar: range + refresh + export */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flex: 1, minWidth: 0 }}>
          {RANGE_DAYS.map(d => (
            <span key={d} data-testid={`oa-range-${d}`}>
              <Chip label={`${d}d`} active={days === d} onClick={() => setDays(d)} />
            </span>
          ))}
        </div>
        <ToolBtn testId="oa-export" onClick={() => downloadText(csvFilename('platform-analytics'), platformCsv(ov, series))}>↓ CSV</ToolBtn>
        <ToolBtn testId="oa-refresh" onClick={() => load(days)} disabled={busy}>{busy ? '…' : '↻'} Refresh</ToolBtn>
      </div>
      <div style={{ fontSize: 9, color: V.text3 }} data-testid="oa-updated">
        {at ? `Updated ${at.toLocaleTimeString()}` : DASH} · last {days} days · counts only, no member emails
      </div>
      {err && <Notice tone="error">Refresh failed ({err}) — showing the last successful load.</Notice>}

      {/* Headline */}
      <TileGrid min={150}>
        <Stat icon={Icons.users} label="Organizations" value={numText(orgs.total)} color={V.accent} sub={`${numText(orgs.active)} active · ${numText(orgs.created_30d)} new (30d)`} />
        <Stat icon={Icons.chart} label="Members in orgs" value={numText(users.in_org)} color={V.accent2} sub={`${pctText(users.in_org_pct)} of ${numText(users.total)} users`} />
        <Stat icon={Icons.zap} label="Billable clients" value={numText(billing.billable_active_clients)} color="#22c55e" sub={billing.month ? `${billing.month} · ${numText(billing.orgs_billable)} orgs` : 'this month'} />
      </TileGrid>

      {/* Cron heartbeat — the one tile that is a misconfiguration, not a metric */}
      <Card style={{ paddingTop: 14, paddingBottom: 14, paddingLeft: 14, paddingRight: 14, border: `1px solid ${hbColor}33` }}>
        <SectionTitle icon={Icons.clock} color={hbColor}>Cron heartbeat</SectionTitle>
        <div data-testid="oa-heartbeat" style={{ marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: hbColor, fontFamily: V.mono }}>{hb.label}</span>
            <span style={{ fontSize: 10, color: V.text3 }}>
              {hb.everRun ? `last run ${hb.age} ago · ${dateText(ov.heartbeat?.last_run_at)}` : 'no run has ever been recorded'}
            </span>
          </div>
          <div style={{ fontSize: 11, color: V.text2, marginTop: 5, lineHeight: 1.5 }}>{hb.detail}</div>
        </div>
      </Card>

      {/* Orgs by status */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.hash} color={V.accent}>Organizations</SectionTitle>
        <TileGrid style={{ marginTop: 8 }}>
          <Tile testId="oa-orgs-total" label="Total" value={numText(orgs.total)} color={V.accent} />
          <Tile label="Active" value={numText(orgs.active)} color="#22c55e" />
          <Tile label="Suspended" value={numText(orgs.suspended)} color={V.warn} />
          <Tile label="Closed" value={numText(orgs.closed)} color={V.danger} />
          <Tile label="New (30d)" value={numText(orgs.created_30d)} sub={`${numText(orgs.created_7d)} in the last 7d`} />
          <Tile label="Setup fee paid" value={numText(billing.orgs_setup_fee_paid)} />
        </TileGrid>
        {orgs.by_kind && Object.keys(orgs.by_kind).length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            {Object.entries(orgs.by_kind).map(([k, n]) => (
              <span key={k} style={{ fontSize: 10, color: V.text3, background: V.hoverBg, borderRadius: 8, paddingTop: 3, paddingBottom: 3, paddingLeft: 8, paddingRight: 8 }}>
                {k}: <strong style={{ color: V.text2 }}>{numText(n)}</strong>
              </span>
            ))}
          </div>
        )}
      </Card>

      {/* Users: org vs solo */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.users} color={V.accent2}>Users</SectionTitle>
        <TileGrid style={{ marginTop: 8 }}>
          <Tile label="Total" value={numText(users.total)} />
          <Tile testId="oa-users-in-org" label="In an org" value={numText(users.in_org)} color={V.accent} sub={pctText(users.in_org_pct)} />
          <Tile label="Solo" value={numText(users.solo)} color={V.text2} />
          <Tile label="Owners / coaches" value={`${numText(members.owners)} / ${numText(members.coaches)}`} />
          <Tile label="Joined (30d)" value={numText(members.joined_30d)} sub={`${numText(members.joined_7d)} in the last 7d`} color="#22c55e" />
          <Tile label="Departed (30d)" value={numText(members.departed_30d)} color={V.warn} />
        </TileGrid>
        <div style={{ marginTop: 10 }}>
          <BarRow label="Share of users in an org" value={users.in_org} pct={users.in_org_pct ?? null} color={V.accent} testId="oa-in-org-bar" />
        </div>
      </Card>

      {/* Seats */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.scale} color={V.purple}>Seats</SectionTitle>
        <TileGrid style={{ marginTop: 8 }}>
          <Tile testId="oa-seats-sold" label="Seats sold" value={numText(seats.seats_sold)} color={V.purple} />
          <Tile label="Seats used" value={numText(seats.member_seats_used)} />
          <Tile testId="oa-seat-util" label="Utilisation" value={pctText(seats.utilization_pct)} color={V.accent} sub={seats.utilization_pct === null || seats.utilization_pct === undefined ? 'no org has a seat limit' : undefined} />
          <Tile label="Unlimited orgs" value={numText(seats.orgs_unlimited)} />
          <Tile label="With a limit" value={numText(seats.orgs_with_limit)} />
          <Tile label="At / over limit" value={numText(seats.orgs_at_or_over_limit)} color={(seats.orgs_at_or_over_limit || 0) > 0 ? V.danger : undefined} />
        </TileGrid>
      </Card>

      {/* Daily series */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.activity} color={V.accent}>Daily activity</SectionTitle>
        {totals.all === 0 ? (
          <EmptyState icon={Icons.calendar} title="No activity in this window" style={{ marginTop: 10 }}>
            Nothing was recorded in the last {days} days — no signups, joins, messages or workouts. Try a wider range.
          </EmptyState>
        ) : (
          <div style={{ marginTop: 8 }} data-testid="oa-series">
            <Label>Org signups · member joins · messages · announcements</Label>
            <div style={{ width: "100%", height: 170 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -22 }}>
                  <defs>
                    {SERIES_KEYS.slice(0, 4).map(([k], i) => (
                      <linearGradient key={k} id={`oa-g-${k}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={colors[i]} stopOpacity={0.55} />
                        <stop offset="100%" stopColor={colors[i]} stopOpacity={0.06} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid {...chartCfg.grid} />
                  <XAxis dataKey="label" {...chartCfg.axis} minTickGap={18} />
                  <YAxis {...chartCfg.axis} allowDecimals={false} width={38} />
                  <Tooltip {...chartCfg.tip} />
                  {SERIES_KEYS.slice(0, 4).map(([k, label], i) => (
                    <Area key={k} type="monotone" dataKey={k} name={label} stackId="a" stroke={colors[i]} strokeWidth={1.5} fill={`url(#oa-g-${k})`} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <ChartLegend items={SERIES_KEYS.slice(0, 4).map(([, label], i) => ({ label, color: colors[i] }))} />

            <Label style={{ marginTop: 12 }}>Workouts — org members vs solo</Label>
            <div style={{ width: "100%", height: 140 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -22 }}>
                  <CartesianGrid {...chartCfg.grid} />
                  <XAxis dataKey="label" {...chartCfg.axis} minTickGap={18} />
                  <YAxis {...chartCfg.axis} allowDecimals={false} width={38} />
                  <Tooltip {...chartCfg.tip} />
                  <Area type="monotone" dataKey="workouts_org" name="Workouts (org)" stackId="w" stroke={colors[4]} strokeWidth={1.5} fill={colors[4]} fillOpacity={0.35} />
                  <Area type="monotone" dataKey="workouts_solo" name="Workouts (solo)" stackId="w" stroke={colors[5]} strokeWidth={1.5} fill={colors[5]} fillOpacity={0.2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <ChartLegend items={[{ label: `Org · ${numText(totals.workouts_org)}`, color: colors[4] }, { label: `Solo · ${numText(totals.workouts_solo)}`, color: colors[5] }]} />
          </div>
        )}
      </Card>

      {/* Consent */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.check} color="#22c55e" right={<span style={{ fontSize: 10, color: V.text3 }}>{numText(ov.consent?.rows)} rows</span>}>Consent opt-in</SectionTitle>
        <div style={{ marginTop: 8 }} data-testid="oa-consent">
          {consentRows(ov.consent).map(r => (
            <BarRow key={r.key} testId={`oa-consent-${r.key}`} label={r.label} value={r.count} pct={r.pct} color="#22c55e" />
          ))}
        </div>
        <div style={{ fontSize: 10, color: V.text3, marginTop: 6 }}>
          Granted (30d): <strong style={{ color: V.text2 }}>{numText(ov.consent?.granted_30d)}</strong> · Revoked (30d): <strong style={{ color: V.text2 }}>{numText(ov.consent?.revoked_30d)}</strong>
        </div>
      </Card>

      {/* Invite funnel */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.target} color={V.warn}>Invite funnel</SectionTitle>
        <div style={{ marginTop: 8 }} data-testid="oa-invites">
          {funnel.map(f => <BarRow key={f.key} testId={`oa-invite-${f.key}`} label={f.label} value={f.value} max={funnelMax} color={V.warn} />)}
        </div>
        <TileGrid style={{ marginTop: 10 }}>
          <Tile testId="oa-redemption-rate" label="Redemption rate" value={pctText(invites.redemption_rate)} color={V.warn} />
          <Tile label="Open" value={numText(invites.open)} />
          <Tile label="Revoked" value={numText(invites.revoked)} />
          <Tile label="Exhausted" value={numText(invites.exhausted)} />
          <Tile label="Expired" value={numText(invites.expired)} />
        </TileGrid>
      </Card>

      {/* Adherence */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.flame} color={V.accent}
          right={<span style={{ fontSize: 10, color: V.text3 }}>avg {pctText(adherence.avg_adherence_pct_28d)}</span>}>Adherence (28d)</SectionTitle>
        {(adherence.clients || 0) === 0 ? (
          <EmptyState icon={Icons.users} title="No coached clients yet" style={{ marginTop: 10 }}>
            Adherence is measured per assigned client — assign a coach to a member to populate this.
          </EmptyState>
        ) : (
          <div style={{ marginTop: 8 }} data-testid="oa-adherence">
            {adherenceRows(adherence).map((b, i) => (
              <BarRow key={b.key} testId={`oa-adherence-${b.key}`} label={b.label} value={b.value} pct={b.pct}
                color={[ '#22c55e', V.warn, '#f97316', V.danger ][i]} />
            ))}
            <div style={{ fontSize: 10, color: V.text3, marginTop: 6 }}>{numText(adherence.clients)} clients</div>
          </div>
        )}
      </Card>

      {/* Coaching + programmes */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.users} color={V.purple}>Coaching engagement</SectionTitle>
        <TileGrid style={{ marginTop: 8 }}>
          <Tile testId="oa-assignments" label="Active assignments" value={numText(coaching.assignments_active)} color={V.purple} />
          <Tile label="Coaches with clients" value={numText(coaching.coaches_with_clients)} />
          <Tile testId="oa-avg-clients" label="Avg clients / coach" value={ratioText(coaching.avg_clients_per_coach)} />
          <Tile label="Clients w/o coach" value={numText(coaching.clients_without_coach)} color={(coaching.clients_without_coach || 0) > 0 ? V.warn : undefined} />
          <Tile label="Coach notes (30d)" value={numText(coaching.notes_30d)} />
          <Tile label="Goals active" value={numText(coaching.goals_active)} sub={`${numText(goals.met)} met · ${numText(goals.dropped)} dropped`} />
          <Tile label="Check-in reviews (30d)" value={numText(checkins.reviews_30d)} sub={`${numText(checkins.coaches_reviewing)} coaches · ${numText(checkins.reviews_total)} all time`} />
          <Tile label="Programmes" value={numText(programs.programs_total)} sub={`${numText(programs.programs_archived)} archived`} />
          <Tile label="Clients on a programme" value={numText(programs.clients_on_program)} sub={`${numText(programs.assignments_active)} active assignments`} />
          <Tile label="Workouts stamped (30d)" value={numText(programs.workouts_stamped_30d)} />
        </TileGrid>
      </Card>

      {/* Messaging */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.bell} color={V.accent2}>Messaging</SectionTitle>
        <TileGrid style={{ marginTop: 8 }}>
          <Tile testId="oa-messages-30d" label="Messages (30d)" value={numText(messaging.messages_30d)} color={V.accent2} sub={`${numText(messaging.messages_7d)} in the last 7d`} />
          <Tile label="Threads" value={numText(messaging.threads)} />
          <Tile label="Messages (all time)" value={numText(messaging.messages_total)} />
          <Tile label="Announcements (30d)" value={numText(messaging.announcements_30d)} />
          <Tile testId="oa-read-rate" label="Announcement read rate" value={pctText(messaging.announcement_read_rate)} />
          <Tile label="Scheduled pending" value={numText(messaging.scheduled_pending)} />
          <Tile label="Scheduled overdue" value={numText(messaging.scheduled_overdue)} color={(messaging.scheduled_overdue || 0) > 0 ? V.danger : undefined} />
        </TileGrid>
      </Card>

      {/* Email queue health */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.upload} color={(queue.failed || 0) > 0 ? V.danger : V.accent}>Email queue</SectionTitle>
        <TileGrid style={{ marginTop: 8 }}>
          <Tile testId="oa-queue-pending" label="Pending" value={numText(queue.pending)} color={(queue.pending || 0) > 0 ? V.warn : undefined} />
          <Tile label="Failed" value={numText(queue.failed)} color={(queue.failed || 0) > 0 ? V.danger : undefined} />
          <Tile label="Sent (24h)" value={numText(queue.sent_24h)} />
          <Tile testId="oa-queue-age" label="Oldest pending" value={ageText(queue.oldest_pending_age_seconds)} />
          <Tile label="Announcement email" value={`${numText(annEmail.sent)} sent`} sub={`${numText(annEmail.pending)} pending · ${numText(annEmail.failed)} failed · ${numText(annEmail.skipped)} skipped`} />
          <Tile label="Audit rows (7d)" value={numText(audit.rows_7d)} sub={`${numText(audit.actors_7d)} actors · ${numText(audit.reads_7d)} reads`} />
        </TileGrid>
        {(queue.failed || 0) > 0 && (
          <Callout icon={Icons.wifiOff} color={V.danger} title="Failed email in the queue" style={{ marginTop: 10 }}>
            {numText(queue.failed)} message(s) have exhausted their retries. They stay in <code>org_email_queue</code> until a run clears them.
          </Callout>
        )}
      </Card>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Per-org
// ════════════════════════════════════════════════════════════════════════════

function PerOrgView({ af }) {
  const [orgs, setOrgs] = useState(null);   // null = loading, [] = none
  const [month, setMonth] = useState('');
  const [listErr, setListErr] = useState('');
  const [q, setQ] = useState('');
  const [orgId, setOrgId] = useState(null);

  const loadList = useCallback(async () => {
    const r = await af('org_list');
    if (r.ok) { setOrgs(r.data?.orgs || []); setMonth(r.data?.month || ''); setListErr(''); }
    else { setListErr(r.msg); setOrgs(o => o ?? []); }
  }, [af]);
  useEffect(() => { loadList(); }, [loadList]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return orgs || [];
    return (orgs || []).filter(o => `${o.name} ${o.slug}`.toLowerCase().includes(needle));
  }, [orgs, q]);

  if (orgId) return <OrgDetail af={af} orgId={orgId} onBack={() => setOrgId(null)} />;

  if (orgs === null) return <Card><Skeleton rows={5} /></Card>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {listErr && <Notice tone="error">Could not load the tenant list ({listErr}).</Notice>}
      {orgs.length === 0 ? (
        <EmptyState icon={Icons.users} title="No organizations yet" action="Retry" onAction={loadList}>
          Create a tenant in Admin → Organizations and its analytics appear here.
        </EmptyState>
      ) : (
        <>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or slug" aria-label="Search organizations"
              style={{
                flex: 1, minWidth: 0, minHeight: 44, background: V.inputBg, border: `1px solid ${V.cardBorder}`, borderRadius: 10,
                paddingTop: 8, paddingBottom: 8, paddingLeft: 10, paddingRight: 10, color: V.text, fontSize: 13, fontFamily: V.font, outline: "none", boxSizing: "border-box",
              }} />
            <ToolBtn onClick={loadList} testId="oa-org-list-refresh">↻</ToolBtn>
          </div>
          <div style={{ fontSize: 9, color: V.text3 }}>{shown.length} of {orgs.length} tenants · billable month {month || DASH}</div>
          {shown.length === 0 ? (
            <EmptyState icon={Icons.search} title="No match">Nothing matches “{q}”.</EmptyState>
          ) : shown.map(o => (
            <div key={o.id} data-testid="oa-org-row">
              <Card onClick={() => setOrgId(o.id)}
                style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12, minHeight: 44 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: V.text }}>{o.name}</span>
                      <StatusPill status={o.status} />
                    </div>
                    <div style={{ fontSize: 10, color: V.text3, marginTop: 2 }}>
                      /{o.slug} · {numText(o.members)} members · {numText(o.coaches)} coaches · {o.active_clients === null || o.active_clients === undefined ? DASH : numText(o.active_clients)} billable
                      {o.seat_limit ? ` · ${numText(o.seat_limit)} seats` : ' · unlimited seats'}
                    </div>
                  </div>
                  {Icons.chevRight({ size: 14, color: V.text3 })}
                </div>
              </Card>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function OrgDetail({ af, orgId, onBack }) {
  const [org, setOrg] = useState(null);
  const [a, setA] = useState(null);         // null = loading
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    const r = await af('org_analytics', { org_id: orgId });
    if (r.ok) { setOrg(r.data?.org || null); setA(r.data?.analytics || {}); setErr(''); }
    else setErr(r.msg);
    setBusy(false);
  }, [af, orgId]);
  useEffect(() => { load(); }, [load]);

  const growth = useMemo(() => (a?.growth || []).map(g => ({ ...g, label: monthLabel(g.month), joins: Number(g.joins) || 0, departures: -(Number(g.departures) || 0) })), [a]);
  const seatRows = useMemo(() => (a?.seats || []).map(x => ({ ...x, label: monthLabel(x.month), active_clients: Number(x.active_clients) || 0 })), [a]);
  const weekRows = useMemo(() => (a?.weeks || []).map(w => ({ ...w, label: monthLabel(w.week_start) + ' ' + String(w.week_start).slice(8), sessions: Number(w.sessions) || 0, clients: Number(w.clients) || 0 })), [a]);

  const back = <ToolBtn onClick={onBack} testId="oa-org-back" ariaLabel="Back to the tenant list">← Tenants</ToolBtn>;

  if (a === null) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 6 }}>{back}</div>
        {err ? <ErrCard msg={err === 'org_not_found' ? 'That organization no longer exists.' : err} onRetry={load} /> : <Card><Skeleton rows={6} /></Card>}
      </div>
    );
  }

  const m = a.members || {}, adh = a.adherence || {}, prog = a.programs || {}, msg = a.messaging || {};
  const inv = a.invites || {}, flags = a.flags || {}, ce = a.consent_events || {};
  const coaches = a.coaches || [];
  const explicitFlags = Object.entries(flags.org || {});
  const funnel = inviteFunnel(inv);
  const funnelMax = Math.max(1, ...funnel.map(f => f.value));
  const topMax = Math.max(1, ...(prog.top_programs || []).map(p => Number(p.assignments) || 0));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="oa-org-detail">
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {back}
        <div style={{ flex: 1, minWidth: 100 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: V.text }}>{org?.name || a.org?.name || 'Organization'}</span>
            <StatusPill status={org?.status || a.org?.status} />
          </div>
          <div style={{ fontSize: 10, color: V.text3 }}>
            /{org?.slug || a.org?.slug} · {org?.kind || a.org?.kind || DASH} · created {dateText(org?.created_at || a.org?.created_at)}
          </div>
        </div>
        <ToolBtn testId="oa-org-export" onClick={() => downloadText(csvFilename(`org-${org?.slug || orgId}`), orgCsv(a))}>↓ CSV</ToolBtn>
        <ToolBtn testId="oa-org-refresh" onClick={load} disabled={busy}>{busy ? '…' : '↻'}</ToolBtn>
      </div>
      {err && <Notice tone="error">Refresh failed ({err}) — showing the last successful load.</Notice>}

      {/* Members + seats */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.users} color={V.accent}>Members</SectionTitle>
        <TileGrid style={{ marginTop: 8 }}>
          <Tile testId="oa-org-members" label="Active" value={numText(m.active_total)} color={V.accent} />
          <Tile label="Owners" value={numText(m.owners)} />
          <Tile label="Coaches" value={numText(m.coaches)} />
          <Tile label="Members" value={numText(m.members)} />
          <Tile label="Departed (30d)" value={numText(m.departed_30d)} color={(m.departed_30d || 0) > 0 ? V.warn : undefined} />
          <Tile testId="oa-org-seat-use" label="Seat use" value={pctText(m.seat_use_pct)} sub={m.seat_limit ? `${numText(m.seat_limit)} seats` : 'unlimited'} />
        </TileGrid>
      </Card>

      {/* Growth by month */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.chart} color="#22c55e">Member growth</SectionTitle>
        {growth.length === 0 ? (
          <EmptyState icon={Icons.calendar} title="No months to show" style={{ marginTop: 10 }}>This tenant has no membership history yet.</EmptyState>
        ) : (
          <div style={{ marginTop: 8, width: "100%", height: 170 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={growth} margin={{ top: 4, right: 4, bottom: 0, left: -22 }} stackOffset="sign">
                <CartesianGrid {...chartCfg.grid} />
                <XAxis dataKey="label" {...chartCfg.axis} minTickGap={10} />
                <YAxis {...chartCfg.axis} allowDecimals={false} width={38} />
                <Tooltip {...chartCfg.tip} formatter={(v, n) => [Math.abs(Number(v) || 0), n]} />
                <Bar dataKey="joins" name="Joins" stackId="g" fill="#22c55e" radius={[3, 3, 0, 0]} />
                <Bar dataKey="departures" name="Departures" stackId="g" fill={V.danger} radius={[0, 0, 3, 3]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <ChartLegend items={[{ label: 'Joins', color: '#22c55e' }, { label: 'Departures', color: V.danger }]} />
      </Card>

      {/* Seat history */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.scale} color={V.purple}>Seat history</SectionTitle>
        {seatRows.length === 0 ? (
          <EmptyState icon={Icons.calendar} title="No snapshots yet" style={{ marginTop: 10 }}>
            The monthly cron freezes a seat snapshot at the end of each month. None has been taken for this tenant.
          </EmptyState>
        ) : (
          <div style={{ marginTop: 8 }} data-testid="oa-org-seats">
            <div style={{ width: "100%", height: 150 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={seatRows} margin={{ top: 4, right: 4, bottom: 0, left: -22 }}>
                  <CartesianGrid {...chartCfg.grid} />
                  <XAxis dataKey="label" {...chartCfg.axis} minTickGap={10} />
                  <YAxis {...chartCfg.axis} allowDecimals={false} width={38} />
                  <Tooltip {...chartCfg.tip} />
                  <Bar dataKey="active_clients" name="Billable clients" radius={[3, 3, 0, 0]}>
                    {seatRows.map((r, i) => (
                      <Cell key={i} fill={r.frozen ? V.purple : `${V.accent2}40`} stroke={r.frozen ? 'none' : V.accent2} strokeWidth={r.frozen ? 0 : 1.5} strokeDasharray={r.frozen ? undefined : '4 2'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <ChartLegend items={[{ label: 'Frozen snapshot', color: V.purple }, { label: 'Current month (live)', color: V.accent2, outline: true }]} />
          </div>
        )}
      </Card>

      {/* Sessions per week */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.dumbbell} color={V.accent2}>Sessions per week</SectionTitle>
        {weekRows.length === 0 || weekRows.every(w => w.sessions === 0) ? (
          <EmptyState icon={Icons.dumbbell} title="No sessions logged" style={{ marginTop: 10 }}>No workouts were logged by this tenant&apos;s members in the last 12 weeks.</EmptyState>
        ) : (
          <div style={{ marginTop: 8, width: "100%", height: 150 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekRows} margin={{ top: 4, right: 4, bottom: 0, left: -22 }}>
                <CartesianGrid {...chartCfg.grid} />
                <XAxis dataKey="label" {...chartCfg.axis} minTickGap={10} />
                <YAxis {...chartCfg.axis} allowDecimals={false} width={38} />
                <Tooltip {...chartCfg.tip} />
                <Bar dataKey="sessions" name="Sessions" fill={V.accent2} radius={[3, 3, 0, 0]} />
                <Bar dataKey="clients" name="Distinct clients" fill={`${V.accent}66`} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <ChartLegend items={[{ label: 'Sessions', color: V.accent2 }, { label: 'Distinct clients', color: V.accent }]} />
      </Card>

      {/* Adherence + coaches */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.flame} color={V.accent}
          right={<span style={{ fontSize: 10, color: V.text3 }}>avg {pctText(adh.avg_adherence_pct_28d)}</span>}>Adherence (28d)</SectionTitle>
        {(adh.clients || 0) === 0 ? (
          <EmptyState icon={Icons.users} title="No coached clients" style={{ marginTop: 10 }}>Assign a coach to a member to start measuring adherence.</EmptyState>
        ) : (
          <div style={{ marginTop: 8 }} data-testid="oa-org-adherence">
            {adherenceRows(adh).map((b, i) => (
              <BarRow key={b.key} label={b.label} value={b.value} pct={b.pct} color={['#22c55e', V.warn, '#f97316', V.danger][i]} />
            ))}
          </div>
        )}
      </Card>

      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.users} color={V.purple}>Coaches</SectionTitle>
        {coaches.length === 0 ? (
          <EmptyState icon={Icons.users} title="No coaches with clients" style={{ marginTop: 10 }}>Nobody in this tenant has an active client assignment.</EmptyState>
        ) : (
          <div style={{ overflowX: "auto", marginTop: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }} data-testid="oa-org-coaches">
              <thead><tr>
                {['Coach', 'Clients', 'Avg 28d', 'On track', 'Slipping', 'At risk', 'Inactive'].map(h => (
                  <th key={h} style={{ ...th, color: V.text3 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {coaches.map(c => (
                  <tr key={c.coach_email}>
                    <td style={{ ...td, color: V.text, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{c.coach_email}</td>
                    <td style={{ ...td, color: V.text2, fontFamily: V.mono }}>{numText(c.clients)}</td>
                    <td style={{ ...td, color: V.text2, fontFamily: V.mono }}>{pctText(c.avg_adherence_pct_28d)}</td>
                    <td style={{ ...td, color: "#22c55e", fontFamily: V.mono }}>{numText(c.on_track)}</td>
                    <td style={{ ...td, color: V.warn, fontFamily: V.mono }}>{numText(c.slipping)}</td>
                    <td style={{ ...td, color: "#f97316", fontFamily: V.mono }}>{numText(c.at_risk)}</td>
                    <td style={{ ...td, color: V.danger, fontFamily: V.mono }}>{numText(c.inactive)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Programmes */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.target} color="#06b6d4">Programme adoption</SectionTitle>
        <TileGrid style={{ marginTop: 8 }}>
          <Tile testId="oa-org-adoption" label="Adoption" value={pctText(prog.adoption_pct)} color="#06b6d4" sub={`${numText(prog.clients_on_program)} on a programme`} />
          <Tile label="Without a programme" value={numText(prog.clients_without_program)} color={(prog.clients_without_program || 0) > 0 ? V.warn : undefined} />
          <Tile label="Programmes" value={numText(prog.programs_total)} sub={`${numText(prog.programs_archived)} archived`} />
          <Tile label="Active assignments" value={numText(prog.assignments_active)} />
          <Tile label="Workouts stamped (30d)" value={numText(prog.workouts_stamped_30d)} />
        </TileGrid>
        <Label style={{ marginTop: 12 }}>Top programmes</Label>
        {(prog.top_programs || []).length === 0 ? (
          <Notice>No programme has an assignment yet.</Notice>
        ) : (prog.top_programs || []).map(p => (
          <BarRow key={p.program_id} label={p.name || p.program_id} value={p.assignments} max={topMax} color="#06b6d4" />
        ))}
      </Card>

      {/* Messaging */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.bell} color={V.accent2}>Messaging</SectionTitle>
        <TileGrid style={{ marginTop: 8 }}>
          <Tile testId="oa-org-messages" label="Messages (30d)" value={numText(msg.messages_30d)} color={V.accent2} sub={`${numText(msg.messages_total)} all time`} />
          <Tile label="Threads" value={numText(msg.threads)} />
          <Tile label="Announcements (30d)" value={numText(msg.announcements_30d)} sub={`${numText(msg.recipients_30d)} recipients`} />
          <Tile testId="oa-org-read-rate" label="Read rate (30d)" value={pctText(msg.read_rate_pct)} sub={`${numText(msg.read_30d)} read`} />
          <Tile label="Scheduled pending" value={numText(msg.scheduled_pending)} />
          <Tile label="Email" value={`${numText(msg.email_sent)} sent`} sub={`${numText(msg.email_pending)} pending · ${numText(msg.email_failed)} failed`} />
        </TileGrid>
      </Card>

      {/* Consent */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.check} color="#22c55e" right={<span style={{ fontSize: 10, color: V.text3 }}>{numText(a.consent?.rows)} rows</span>}>Consent</SectionTitle>
        <div style={{ marginTop: 8 }} data-testid="oa-org-consent">
          {consentRows(a.consent).map(r => <BarRow key={r.key} label={r.label} value={r.count} pct={r.pct} color="#22c55e" />)}
        </div>
        <Label style={{ marginTop: 12 }}>Revocations</Label>
        <TileGrid>
          <Tile label="Granted (30d)" value={numText(ce.granted_30d)} color="#22c55e" />
          <Tile label="Revoked (30d)" value={numText(ce.revoked_30d)} color={(ce.revoked_30d || 0) > 0 ? V.danger : undefined} />
          <Tile label="Revoked (all time)" value={numText(ce.revoked_total)} />
        </TileGrid>
        {revokedRows(ce.revoked_by_scope).some(r => r.count > 0) ? (
          <div style={{ marginTop: 8 }}>
            {revokedRows(ce.revoked_by_scope).filter(r => r.count > 0).map(r => (
              <BarRow key={r.key} label={`Revoked · ${r.label}`} value={r.count} max={Math.max(1, ...revokedRows(ce.revoked_by_scope).map(x => x.count))} color={V.danger} />
            ))}
          </div>
        ) : <Notice style={{ marginTop: 8 }}>No scope has been revoked.</Notice>}
      </Card>

      {/* Invites */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.target} color={V.warn}>Invites</SectionTitle>
        <div style={{ marginTop: 8 }}>
          {funnel.map(f => <BarRow key={f.key} label={f.label} value={f.value} max={funnelMax} color={V.warn} />)}
        </div>
        <TileGrid style={{ marginTop: 10 }}>
          <Tile testId="oa-org-redemption" label="Redemption rate" value={pctText(inv.redemption_rate)} color={V.warn} />
          <Tile label="Open" value={numText(inv.open)} />
          <Tile label="Created (30d)" value={numText(inv.created_30d)} />
        </TileGrid>
      </Card>

      {/* Flags */}
      <Card style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 12, paddingRight: 12 }}>
        <SectionTitle icon={Icons.sliders} color={V.text2}>Feature overrides</SectionTitle>
        <TileGrid style={{ marginTop: 8 }}>
          <Tile testId="oa-org-flag-rows" label="Org-level rows" value={numText(flags.org_rows)} />
          <Tile label="Client overrides" value={numText(flags.client_overrides)} />
          <Tile label="Clients with an override" value={numText(flags.clients_with_overrides)} />
        </TileGrid>
        <Label style={{ marginTop: 12 }}>Explicit org flags</Label>
        {explicitFlags.length === 0 ? (
          <Notice>Every flag is on its default — nothing has been pinned for this tenant.</Notice>
        ) : (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="oa-org-flags">
            {explicitFlags.map(([k, on]) => (
              <span key={k} style={{
                fontSize: 10, fontWeight: 700, fontFamily: V.mono, borderRadius: 8,
                paddingTop: 4, paddingBottom: 4, paddingLeft: 8, paddingRight: 8,
                color: on ? '#22c55e' : V.danger, background: `${on ? '#22c55e' : V.danger}14`,
                border: `1px solid ${on ? '#22c55e' : V.danger}28`,
              }}>{k} · {on ? 'on' : 'off'}</span>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Shared failure card ───
function ErrCard({ msg, onRetry }) {
  return (
    <Card style={{ paddingTop: 16, paddingBottom: 16, paddingLeft: 16, paddingRight: 16 }}>
      <Callout icon={Icons.wifiOff} color={V.danger} title="Could not load analytics">
        <span style={{ fontFamily: V.mono }}>{msg}</span>
      </Callout>
      <div style={{ marginTop: 12 }}><Btn v="secondary" full onClick={onRetry}>Retry</Btn></div>
    </Card>
  );
}

export default OrgAnalytics;
